import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { fmtAUD } from '@/lib/format';
import { useCart } from '@/context/CartContext';
import { toast, Toaster } from 'sonner';
import { ArrowLeft, Plus, Minus } from 'lucide-react';

export default function ProductDetail() {
    const { slug } = useParams();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [quantities, setQuantities] = useState({});
    const [colourFilter, setColourFilter] = useState('all');
    const [audienceFilter, setAudienceFilter] = useState('all');
    const [activeImage, setActiveImage] = useState(null);

    useEffect(() => {
        api.get(`/products/${slug}`)
            .then((r) => {
                setProduct(r.data);
                setActiveImage(r.data.image_url);
            })
            .finally(() => setLoading(false));
    }, [slug]);

    const colours = useMemo(() => {
        if (!product) return [];
        return [...new Set(product.variants.map((v) => v.colour).filter(Boolean))];
    }, [product]);

    const audiences = useMemo(() => {
        if (!product) return [];
        return [...new Set(product.variants.map((v) => v.audience).filter(Boolean))];
    }, [product]);

    const filteredVariants = useMemo(() => {
        if (!product) return [];
        return product.variants.filter((v) => {
            if (colourFilter !== 'all' && v.colour !== colourFilter) return false;
            if (audienceFilter !== 'all' && v.audience !== audienceFilter) return false;
            return true;
        });
    }, [product, colourFilter, audienceFilter]);

    const bump = (sku, delta) => {
        setQuantities((q) => ({ ...q, [sku]: Math.max(0, (q[sku] || 0) + delta) }));
    };

    const setQ = (sku, value) => {
        const n = parseInt(value, 10);
        setQuantities((q) => ({ ...q, [sku]: Number.isNaN(n) || n < 0 ? 0 : n }));
    };

    const addAll = () => {
        let added = 0;
        Object.entries(quantities).forEach(([sku, qty]) => {
            if (qty > 0) {
                const variant = product.variants.find((v) => v.sku === sku);
                if (variant) {
                    addItem(product, variant, qty);
                    added += qty;
                }
            }
        });
        if (added === 0) {
            toast.error('Enter at least one quantity');
        } else {
            toast.success(`Added ${added} item(s) to cart`);
            setQuantities({});
        }
    };

    const lineTotal = useMemo(() => {
        if (!product) return 0;
        return Object.entries(quantities).reduce((s, [_, q]) => s + (q || 0) * product.price, 0);
    }, [quantities, product]);

    if (loading) return <div className="text-zinc-400 font-mono-data">Loading…</div>;
    if (!product) return <div className="text-zinc-400">Not found.</div>;

    return (
        <div data-testid={`product-detail-${slug}`}>
            <Toaster theme="dark" />
            <Link to="/" className="font-mono-data text-xs uppercase text-zinc-500 hover:text-crimson inline-flex items-center gap-2 mb-6">
                <ArrowLeft size={14} /> Back to catalog
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
                <div className="lg:col-span-7">
                    <div className="border-hairline relative overflow-hidden bg-[#e9e7e6]">
                        <img
                            data-testid="product-main-image"
                            src={activeImage || product.image_url}
                            alt={product.name}
                            className="w-full aspect-[4/3] object-contain"
                        />
                    </div>
                    {product.gallery && product.gallery.length > 1 && (
                        <div className="grid grid-cols-4 gap-2 mt-2" data-testid="product-gallery">
                            {product.gallery.map((url, i) => (
                                <button
                                    key={url}
                                    data-testid={`gallery-thumb-${i}`}
                                    onClick={() => setActiveImage(url)}
                                    className={`border bg-[#e9e7e6] overflow-hidden transition-colors ${
                                        activeImage === url ? 'border-brand' : 'border-zinc-800 hover:border-zinc-600'
                                    }`}
                                >
                                    <img src={url} alt={`view ${i + 1}`} className="w-full aspect-square object-contain" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>
                <div className="lg:col-span-5">
                    <div className="font-mono-data text-xs uppercase text-crimson tracking-widest mb-3">
                        {product.category}
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl uppercase tracking-tighter leading-none mb-4">
                        {product.name}
                    </h1>
                    <p className="font-body text-zinc-300 mb-8">{product.description}</p>

                    <div className="grid grid-cols-2 border-hairline">
                        <div className="p-5 border-r border-zinc-800">
                            <div className="font-mono-data text-[10px] uppercase text-zinc-500">Your price</div>
                            <div className="font-mono-data text-2xl text-white">{fmtAUD(product.price)}</div>
                        </div>
                        <div className="p-5">
                            <div className="font-mono-data text-[10px] uppercase text-zinc-500">Suggested RRP</div>
                            <div className="font-mono-data text-2xl text-white">
                                {product.rrp ? fmtAUD(product.rrp) : '—'}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Variant table */}
            <div className="border-hairline">
                <div className="flex flex-wrap items-center justify-between gap-4 p-5 border-b border-zinc-800">
                    <h2 className="font-display text-2xl uppercase tracking-tighter">Sizes & Quantities</h2>
                    <div className="flex flex-wrap gap-2">
                        {audiences.length > 1 && (
                            <select
                                data-testid="filter-audience"
                                value={audienceFilter}
                                onChange={(e) => setAudienceFilter(e.target.value)}
                                className="bg-surface border border-zinc-800 px-3 py-2 text-sm font-mono-data text-white">
                                <option value="all">All audiences</option>
                                {audiences.map((a) => <option key={a} value={a}>{a}</option>)}
                            </select>
                        )}
                        {colours.length > 0 && (
                            <select
                                data-testid="filter-colour"
                                value={colourFilter}
                                onChange={(e) => setColourFilter(e.target.value)}
                                className="bg-surface border border-zinc-800 px-3 py-2 text-sm font-mono-data text-white">
                                <option value="all">All colours</option>
                                {colours.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-12 gap-0 font-mono-data text-[10px] uppercase text-zinc-500 px-5 py-3 border-b border-zinc-800 bg-surface">
                    <div className="col-span-6">Size / Variant</div>
                    <div className="col-span-3 text-center">Qty</div>
                    <div className="col-span-3 text-right">Line total</div>
                </div>

                <div className="max-h-[60vh] overflow-y-auto">
                    {filteredVariants.map((v) => {
                        const q = quantities[v.sku] || 0;
                        return (
                            <div key={v.sku} data-testid={`variant-row-${v.sku}`}
                                 className="grid grid-cols-12 items-center px-5 py-3 border-b border-zinc-800/60 hover:bg-surface/60 transition-colors">
                                <div className="col-span-6 font-body text-sm">{v.label}</div>
                                <div className="col-span-3 flex items-center justify-center gap-2">
                                    <button data-testid={`qty-minus-${v.sku}`} onClick={() => bump(v.sku, -1)}
                                            className="p-1 border border-zinc-800 hover:border-crimson hover:text-crimson">
                                        <Minus size={12} />
                                    </button>
                                    <input
                                        data-testid={`qty-input-${v.sku}`}
                                        type="number" min="0"
                                        value={q || ''}
                                        onChange={(e) => setQ(v.sku, e.target.value)}
                                        placeholder="0"
                                        className="w-16 bg-surface border border-zinc-800 px-2 py-1 text-center font-mono-data text-sm" />
                                    <button data-testid={`qty-plus-${v.sku}`} onClick={() => bump(v.sku, 1)}
                                            className="p-1 border border-zinc-800 hover:border-crimson hover:text-crimson">
                                        <Plus size={12} />
                                    </button>
                                </div>
                                <div className="col-span-3 text-right font-mono-data text-sm">
                                    {fmtAUD(q * product.price)}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between p-5 border-t border-zinc-800 bg-surface">
                    <div>
                        <div className="font-mono-data text-[10px] uppercase text-zinc-500">Add-to-cart subtotal</div>
                        <div data-testid="line-subtotal" className="font-mono-data text-2xl">{fmtAUD(lineTotal)}</div>
                    </div>
                    <button
                        data-testid="add-to-cart-btn"
                        onClick={addAll}
                        className="bg-brand hover:bg-brand-hover text-black font-display uppercase tracking-wider px-6 py-3 transition-colors">
                        Add to Cart
                    </button>
                </div>
            </div>
        </div>
    );
}
